import { Box, IconButton, Stack, Typography } from '@mui/material';
import {
  DATE_FORMAT_MONTH_FNS,
  getTypedObjectKeys,
  INVOICE_ACTION_MUTATION,
  PayableFee,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useFlag } from '@schooly/hooks/use-flag';
import { ContextMenu, EditIcon, EmptyListSvg, MoreIcon, UnpublishIcon } from '@schooly/style';
import {
  ChevronUpIcon,
  Counter,
  Price,
  PRICE_SUBTEXT_CLASS_NAME,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { useIsMutating } from '@tanstack/react-query';
import { closestTo, format, getMonth, getYear, isAfter, startOfMonth } from 'date-fns';
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useWithRef } from '../../../hooks/useWithRef';
import { EDITABLE_INVOICE_STATUSES } from '../../../pages/Invoices/InvoiceEditModal';
import { getCurrencySymbol } from '../../../pages/School/SchoolProducts/helpers';
import { VoidInvoiceModal } from '../Invoices/VoidInvoiceModalContent';
import { payableFeeHasInvoice } from './helpers';
import { PayableFeesRelationType } from './PayableFees';
import { PayableFeeTable, PayableFeeTableProps } from './PayableFeeTable';

type PayableFeesContentProps = {
  data: PayableFee[];
  relationType: PayableFeesRelationType;
  schoolId: string;
  onVoidInvoice?: (invoiceId: string) => Promise<any>;
};

export const PayableFeesContent: FC<PayableFeesContentProps> = ({
  data,
  relationType,
  schoolId,
  onVoidInvoice,
}) => {
  const payableFeesMap = useMemo(
    () =>
      data
        .sort((a, b) => {
          const dateA = newDateTimezoneOffset(b.issue_date);
          const dateB = newDateTimezoneOffset(a.issue_date);
          const byYear = getYear(dateA) - getYear(dateB);
          if (byYear !== 0) return byYear;

          return getMonth(dateA) - getMonth(dateB);
        })
        .reduce<Record<string, PayableFee[]>>((acc, next) => {
          const date = newDateTimezoneOffset(next.issue_date);
          const label = `${format(date, DATE_FORMAT_MONTH_FNS)} ${getYear(date)}`;
          const data = acc[label];

          return { ...acc, [label]: data ? [...data, next] : [next] };
        }, {}),
    [data],
  );

  const monthLabels = getTypedObjectKeys(payableFeesMap);
  const { $t } = useIntl();

  const defaultMonthLabel = useMemo(() => {
    if (monthLabels.length === 1) {
      return monthLabels[0];
    }

    const currentMonth = startOfMonth(newDateTimezoneOffset());
    const currentMonthLabel = `${format(currentMonth, DATE_FORMAT_MONTH_FNS)} ${getYear(
      currentMonth,
    )}`;

    if (!monthLabels.length || monthLabels.includes(currentMonthLabel)) {
      return currentMonthLabel;
    }

    const labels = monthLabels.filter((label) => payableFeesMap[label][0].issue_date);
    const dates = labels.map((label) =>
      startOfMonth(newDateTimezoneOffset(payableFeesMap[label][0].issue_date)),
    );
    const futureDates = dates.filter((date) => isAfter(date, currentMonth));
    const closestDate = closestTo(currentMonth, futureDates) ?? closestTo(currentMonth, dates);

    return closestDate
      ? `${format(closestDate, DATE_FORMAT_MONTH_FNS)} ${getYear(closestDate)}`
      : currentMonthLabel;
  }, [monthLabels, payableFeesMap]);

  const isMultipleItemsView = relationType === 'parent';

  if (!monthLabels.length) {
    return (
      <Stack flex={1} alignItems="center" gap={2.5} p={2} pt={5}>
        <EmptyListSvg />
        <Typography textAlign="center" variant="h3" color="text.primary" maxWidth={280}>
          {$t({ id: 'profile-NoResultsMatching' })}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack gap={3.75}>
      {monthLabels.map((monthLabel, i) => {
        const isDefaultMonth = defaultMonthLabel === monthLabel;
        const isFirstMonth = i === 0;

        return (
          <Box key={monthLabel}>
            <MonthAccordion
              monthLabel={monthLabel}
              payableFees={payableFeesMap[monthLabel]}
              opened={isDefaultMonth}
              scrollTop={isDefaultMonth && !isFirstMonth}
            >
              {(opened) =>
                opened ? (
                  <Stack gap={3} pt={1.5}>
                    {payableFeesMap[monthLabel].map((payableFee, idx) => {
                      const [student] = payableFee.students;
                      const key = payableFee.invoice_id
                        ? `${payableFee.invoice_id}-${student ? student.id : ''}`
                        : `${payableFee.invoice_date}-${idx}-${student ? student.id : ''}`;

                      return (
                        <PayableFeeItemContent
                          key={key}
                          hasInvoice={payableFeeHasInvoice(payableFee)}
                          payableFee={payableFee}
                          relationType={relationType}
                          isMultipleItemsView={isMultipleItemsView}
                          schoolId={schoolId}
                          onVoidInvoice={onVoidInvoice}
                        />
                      );
                    })}
                  </Stack>
                ) : null
              }
            </MonthAccordion>
          </Box>
        );
      })}
    </Stack>
  );
};

type MonthAccordionProps = {
  payableFees: PayableFee[];
  monthLabel: string;
  children: (opened: boolean) => ReactNode;
  opened: boolean;
  scrollTop?: boolean;
};

export const MonthAccordion: FC<MonthAccordionProps> = ({
  monthLabel,
  payableFees,
  children,
  opened: initialOpened,
  scrollTop,
}) => {
  const [opened, open, hide] = useFlag(initialOpened);
  const ref = useRef<HTMLDivElement>(null);
  const { containerRef: mainContainerRef } = useWithRef();

  const { sum, counter } = useMemo(
    () => ({
      sum: payableFees.reduce<Record<string, number>>(
        (acc, { currency, total_payment }) => {
          const symbol = getCurrencySymbol(currency);
          const total = acc[symbol] ? acc[symbol] + total_payment : total_payment;

          return { ...acc, [symbol]: total };
        },

        {},
      ),
      counter: payableFees.reduce((acc, next) => {
        let items = next.items;
        if (payableFeeHasInvoice(next)) {
          items = next.items.filter((item) => !item.is_deleted);
        }
        return acc + items.length;
      }, 0),
    }),
    [payableFees],
  );

  useEffect(() => {
    if (scrollTop && mainContainerRef?.current) {
      const top =
        (ref.current?.getBoundingClientRect().top ?? 0) -
        mainContainerRef.current.getBoundingClientRect().top;

      mainContainerRef?.current?.scrollTo({
        top: top > 0 ? top : undefined,
        behavior: 'smooth',
      });
    }
  }, [mainContainerRef, scrollTop]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        onClick={opened ? hide : open}
        ref={ref}
        alignItems="center"
        sx={(theme) => ({
          ...(opened && {
            [` .${PRICE_SUBTEXT_CLASS_NAME}`]: {
              color: 'common.grey2',
            },
          }),

          '& .counter': {
            ...(!opened && {
              color: 'primary.main',
              bgcolor: 'transparent',
              border: `1px solid ${theme.palette.primary.main}`,
            }),
          },

          ':hover': {
            cursor: 'pointer',
            [` .${PRICE_SUBTEXT_CLASS_NAME}`]: {
              color: 'common.grey2',
            },
            '.arrowIcon': {
              color: 'primary.main',
            },
          },
        })}
      >
        <Stack direction="row" alignItems="center">
          <TypographyWithOverflowHint variant="h3" width={76}>
            {monthLabel.toLocaleUpperCase()}
          </TypographyWithOverflowHint>
          <Counter
            className="counter"
            sx={{ minWidth: 20, minHeight: 20, fontWeight: 500, textAlign: 'center', ml: 0 }}
          >
            {counter}
          </Counter>
        </Stack>

        <Stack direction="row" alignItems="center" pr={1} gap={6.25}>
          <Stack direction="row" alignItems="center">
            {Object.entries(sum).map(([currency, priceNumber], i, arr) => {
              const price = (
                <Price
                  currency={currency}
                  price={priceNumber}
                  color={opened ? 'primary.main' : 'common.grey2'}
                />
              );
              if (arr.length === 1 || i === 0) return price;

              return (
                <React.Fragment key={currency}>
                  {i !== arr.length - 1 ? (
                    <>
                      {' ,'}
                      {price}
                    </>
                  ) : (
                    <Stack direction="row" alignItems="center">
                      <Typography variant="h3" px={1} color="common.grey2">
                        &
                      </Typography>
                      {price}
                    </Stack>
                  )}
                </React.Fragment>
              );
            })}
          </Stack>

          <IconButton
            size="small"
            className="arrowIcon"
            sx={(theme) => ({
              transform: !opened ? 'rotate(180deg)' : undefined,
              color: opened ? theme.palette.primary.main : undefined,
            })}
            inverse
          >
            <ChevronUpIcon />
          </IconButton>
        </Stack>
      </Stack>
      {children(opened)}
    </>
  );
};

type PayableFeeItemContentProps = {
  onVoidInvoice?: (invoiceId: string) => Promise<any>;
} & Omit<PayableFeeTableProps, 'contextMenu'>;

const PayableFeeItemContent: FC<PayableFeeItemContentProps> = ({ onVoidInvoice, ...props }) => {
  const { status, invoice_id } = props.payableFee;

  const { permissions } = useAuth();
  const canEditInvoice = permissions.includes('product_and_invoice_manager');
  const isUpcoming = status === 'upcoming';
  const isCancelled = status === 'cancelled';
  const isVoided = status === 'voided';
  const isInvoiceMutating = Boolean(useIsMutating({ mutationKey: [INVOICE_ACTION_MUTATION] }));
  const [invoiceVoidModalOpened, openInvoiceVoidModal, closeInvoiceVoidModal] = useFlag();
  const navigate = useNavigate();
  const canShowContextMenu =
    !isVoided && !isUpcoming && !isCancelled && !!invoice_id && canEditInvoice;

  const invoice = payableFeeHasInvoice(props.payableFee) ? props.payableFee : null;

  const canVoidInvoice =
    invoice && !invoice.total_paid ? EDITABLE_INVOICE_STATUSES.includes(invoice.status) : false;

  const handleVoidInvoice = useCallback(async () => {
    if (!invoice) return;
    openInvoiceVoidModal();
  }, [invoice, openInvoiceVoidModal]);

  return (
    <>
      <PayableFeeTable
        {...props}
        contextMenu={
          canShowContextMenu ? (
            <ContextMenu
              PopperProps={{ disablePortal: false }}
              placement="bottom"
              arrow={false}
              componentsProps={{
                tooltip: {
                  sx: {
                    minWidth: 215,
                    '&&.MuiTooltip-tooltip': {
                      mt: 0.25,
                    },
                    '.container': {
                      gap: 1.5,
                      paddingX: 0,
                    },
                  },
                },
              }}
              actions={[
                {
                  labelTextId: 'payableFees-EditInvoice',
                  icon: <EditIcon />,
                  onClick: (close) => {
                    navigate(`/invoices/${invoice_id}/edit`);
                    close?.();
                  },
                },
                ...(canVoidInvoice
                  ? [
                      {
                        labelTextId: 'payableFees-VoidInvoice',
                        icon: <UnpublishIcon />,
                        onClick: (close?: () => void) => {
                          handleVoidInvoice();
                          close?.();
                        },
                      },
                    ]
                  : []),
              ]}
            >
              {(open) => (
                <IconButton inverse onClick={open}>
                  <MoreIcon />
                </IconButton>
              )}
            </ContextMenu>
          ) : undefined
        }
      />

      <VoidInvoiceModal
        opened={invoiceVoidModalOpened}
        onClose={closeInvoiceVoidModal}
        isLoading={isInvoiceMutating}
        onSubmit={async () => {
          if (!invoice) return;
          await onVoidInvoice?.(invoice.invoice_id);
          closeInvoiceVoidModal();
        }}
        {...props}
      />
    </>
  );
};
